import { useCallback, useMemo, useState } from "react";
import { Check, ChevronRight } from "lucide-react";
import type { CatalogPrice } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Button, Text } from "@ferrivia/ui";
import { useTabNavigation } from "../../app/useTabNavigation";
import { useLabAddOnGroups, type LabAddOn } from "./labAddOns";
import s from "./IronPanelPurchase.module.css";

/** The iron panel every lab order is built on: ferritin and a sensitive CRP.
    Fixed rather than selectable — it is what a Ferrivia lab order *is*, and
    the add-ons are added to it. */
export const IRON_PANEL_SLUG = "raudanpuutteen-laboratoriopaketti";

export interface IronPanelPurchaseProps {
  /** Active price per catalog slug, resolved by IronPanelPurchaseSheet. */
  priceBySlug: Map<string, CatalogPrice>;
  disabled?: boolean;
  onOrder: (lookupKeys: string[]) => Promise<void>;
  /** Called after the help CTA switches to the chat tab. The sheet portals
      outside the tab panels, so it has to close itself or it would stay on
      top of the chat. */
  onChatOpened: () => void;
}

/**
 * Build a lab panel: the iron panel plus whichever add-ons the client ticks.
 *
 * Everything on this page is the same for every client. No row is preselected,
 * reordered, highlighted or priced from anything they have measured, and no row
 * says what a test is for — see labAddOns.ts for why that is a hard line rather
 * than a copy preference.
 */
export function IronPanelPurchase({ priceBySlug, disabled, onOrder, onChatOpened }: IronPanelPurchaseProps) {
  const { t } = useTranslation("client");
  const { currencyEur } = useFormatters();
  const tracker = useTracker();
  const { goToTab } = useTabNavigation();
  const groups = useLabAddOnGroups();

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const corePrice = priceBySlug.get(IRON_PANEL_SLUG);

  // An add-on with no active price is dropped rather than shown priceless —
  // a slug that never mirrored into the catalog is not something to sell. A
  // group left with nothing in it goes too.
  const availableGroups = useMemo(
    () =>
      groups
        .map((group) => ({ ...group, addOns: group.addOns.filter((addOn) => priceBySlug.has(addOn.slug)) }))
        .filter((group) => group.addOns.length > 0),
    [groups, priceBySlug],
  );

  const selectedAddOns = useMemo(
    () => availableGroups.flatMap((group) => group.addOns).filter((addOn) => selected.has(addOn.slug)),
    [availableGroups, selected],
  );

  const totalCents =
    (corePrice?.unitAmountCents ?? 0) +
    selectedAddOns.reduce((sum, addOn) => sum + (priceBySlug.get(addOn.slug)?.unitAmountCents ?? 0), 0);

  const handleToggle = useCallback(
    (addOn: LabAddOn) => {
      setSelected((previous) => {
        const next = new Set(previous);
        const nowSelected = !next.has(addOn.slug);
        if (nowSelected) next.add(addOn.slug);
        else next.delete(addOn.slug);

        const lookupKey = priceBySlug.get(addOn.slug)?.lookupKey;
        if (lookupKey) {
          tracker.track("store.lab_addon_toggled", {
            lookup_key: lookupKey,
            selected: nowSelected,
            // The whole panel's size after the change: the iron panel plus
            // whatever is ticked, so a session reads without replaying every
            // toggle.
            item_count: next.size + 1,
          });
        }
        return next;
      });
    },
    [priceBySlug, tracker],
  );

  async function handleOrder() {
    if (!corePrice) return;
    setPending(true);
    setFailed(false);
    const lookupKeys = [
      corePrice.lookupKey,
      ...selectedAddOns.map((addOn) => priceBySlug.get(addOn.slug)!.lookupKey),
    ];
    try {
      await onOrder(lookupKeys);
    } catch {
      setPending(false);
      setFailed(true);
    }
  }

  function handleAskTeam() {
    tracker.track("store.lab_help_chat_opened", {});
    goToTab("/chat", "in_app_link");
    onChatOpened();
  }

  // No iron panel in the catalog means there is no lab order to build. Shown
  // rather than rendered blank so the sheet does not read as broken.
  if (!corePrice) {
    return (
      <Text as="p" size="sm" className={s.intro}>
        {t("store.lab.unavailable", "Laboratoriopaketit eivät ole juuri nyt saatavilla.")}
      </Text>
    );
  }

  return (
    <>
      <Text as="p" size="sm" className={s.intro}>
        {t("store.lab.intro", "Kaikki valitsemasi kokeet otetaan yhdellä käynnillä laboratoriossa.")}
      </Text>

      <div className={s.core}>
        <div className={s.coreMain}>
          <span className={s.coreName}>{t("store.products.labPanel.name", "Laboratoriopaketti")}</span>
          <span className={s.coreIncludes}>
            {t("store.lab.coreIncludes", "Ferritiini · Herkkä CRP · sisältyy aina")}
          </span>
        </div>
        <span className={s.corePrice}>{currencyEur.format(corePrice.unitAmountCents / 100)}</span>
      </div>

      {availableGroups.map((group) => (
        <div key={group.id} className={s.group}>
          <span className={s.groupLabel}>{group.label}</span>
          {group.addOns.map((addOn) => {
            const price = priceBySlug.get(addOn.slug)!;
            return (
              <label key={addOn.slug} className={s.test}>
                <input
                  type="checkbox"
                  className={s.input}
                  checked={selected.has(addOn.slug)}
                  disabled={disabled || pending}
                  onChange={() => handleToggle(addOn)}
                />
                <span className={s.box} aria-hidden="true">
                  <Check strokeWidth={3} />
                </span>
                <span className={s.testMain}>
                  <span className={s.testName}>{addOn.name}</span>
                  <span className={s.testCode}>{addOn.code}</span>
                </span>
                <span className={s.testPrice}>+{currencyEur.format(price.unitAmountCents / 100)}</span>
              </label>
            );
          })}
        </div>
      ))}

      <div className={s.totalBar}>
        <span className={s.totalSum}>
          <span className={s.totalLabel}>{t("store.lab.total", "Yhteensä")}</span>
          <span className={s.totalAmount}>{currencyEur.format(totalCents / 100)}</span>
        </span>
        <Button variant="primary" disabled={disabled || pending} onClick={handleOrder}>
          {pending ? t("store.cta.ordering", "Tilataan…") : t("store.cta.order", "Tilaa")}
        </Button>
      </div>

      {failed && (
        <Text as="p" size="sm" className={s.error}>
          {t("store.error", "Kassalle siirtyminen epäonnistui. Yritä uudestaan.")}
        </Text>
      )}

      <button type="button" className={s.askTeam} onClick={handleAskTeam}>
        <span className={s.askTeamText}>{t("store.lab.askTeam", "Kysyttävää labroista?")}</span>
        <span className={s.askTeamGo}>
          {t("store.lab.askTeamCta", "Avaa chat")}
          <ChevronRight aria-hidden="true" />
        </span>
      </button>
    </>
  );
}
